import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, XLg } from 'react-bootstrap-icons';
import employeeForm4 from '../../assets/images/employees/employeeform4.png';
import { apiRequest } from '../../utils/api';
import { resolveFileFromCandidates } from '../../utils/fileUrl';

const emptyQualificationDraft = {
  qualificationType: '',
  courseName: '',
  institutionName: '',
  yearOfPassing: '',
  gradeOrPercentage: '',
  file: null,
  fileName: '',
};

const createLocalId = () => `qualification-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const mapQualificationEntries = (entries) =>
  Array.isArray(entries)
    ? entries.map((entry) => ({
        ...entry,
        qualificationType: entry.qualificationType ?? '',
        courseName: entry.courseName ?? '',
        institutionName: entry.institutionName ?? '',
        yearOfPassing: entry.yearOfPassing ?? '',
        gradeOrPercentage: entry.gradeOrPercentage ?? '',
        documentPath: entry.documentPath ?? '',
        documentUrl: entry.documentUrl ?? '',
        fileName: entry.fileName ?? '',
        localId: entry.localId || entry.id || createLocalId(),
      }))
    : [];

const sanitizeQualificationEntries = (entries) =>
  entries.map(({ localId, file, documentUrl, ...rest }) => rest);

const EducationForm = ({ onSubmit, data, enableValidation = false }) => {
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [qualificationOptionsError, setQualificationOptionsError] = useState('');
  const [qualificationEntries, setQualificationEntries] = useState(
    mapQualificationEntries(data?.qualifications),
  );
  const [hasQualification, setHasQualification] = useState(
    Boolean(data?.hasQualification ?? (data?.qualifications?.length)),
  );
  const [qualificationDraft, setQualificationDraft] = useState(emptyQualificationDraft);
  const [qualificationError, setQualificationError] = useState('');
  const [isQualificationUploading, setIsQualificationUploading] = useState(false);
  const qualificationFileInputRef = useRef(null);

  useEffect(() => {
    setQualificationEntries(mapQualificationEntries(data?.qualifications));
    setHasQualification(Boolean(data?.hasQualification ?? (data?.qualifications?.length)));
    setQualificationError('');
    setQualificationDraft(emptyQualificationDraft);
    if (qualificationFileInputRef.current) {
      qualificationFileInputRef.current.value = '';
    }
  }, [data]);

  useEffect(() => {
    let isMounted = true;

    const loadQualificationOptions = async () => {
      try {
        setQualificationOptionsError('');
        const response = await apiRequest('/api/public/education-qualifications', {
          skipAuthRedirect: true,
        });
        if (!isMounted) {
          return;
        }
        const list = Array.isArray(response.data) ? response.data : [];
        setQualificationOptions(list);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setQualificationOptions([]);
        setQualificationOptionsError(
          error.message || 'Failed to load education qualification options.',
        );
      }
    };

    loadQualificationOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const resetQualificationDraft = () => {
    setQualificationDraft(emptyQualificationDraft);
    if (qualificationFileInputRef.current) {
      qualificationFileInputRef.current.value = '';
    }
  };

  const handleQualificationDraftChange = (event) => {
    const { name, value, files } = event.target;
    if (name === 'file') {
      const file = files && files.length > 0 ? files[0] : null;
      setQualificationDraft((prev) => ({
        ...prev,
        file,
        fileName: file ? file.name.toUpperCase() : '',
      }));
      setQualificationError('');
      return;
    }

    let nextValue = value;
    if (name === 'yearOfPassing') {
      nextValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setQualificationDraft((prev) => ({
      ...prev,
      [name]: typeof nextValue === 'string' ? nextValue.toUpperCase() : nextValue,
    }));
    setQualificationError('');
  };

  const resolveQualificationLabel = useMemo(
    () =>
      qualificationOptions.reduce((accumulator, option) => {
        if (!option) {
          return accumulator;
        }
        const code = (option.code || '').toUpperCase();
        if (!code) {
          return accumulator;
        }
        accumulator[code] = (option.title || option.code || '').toUpperCase();
        return accumulator;
      }, {}),
    [qualificationOptions],
  );

  const getQualificationLabel = (code) => {
    const upperCode = (code || '').toUpperCase();
    return resolveQualificationLabel[upperCode] || upperCode || 'QUALIFICATION';
  };

  const resolveDocumentUrl = (entry) => {
    return resolveFileFromCandidates(entry?.documentUrl, entry?.documentPath);
  };

  const handleAddQualification = async () => {
    if (isQualificationUploading) {
      return;
    }

    const {
      qualificationType,
      courseName,
      institutionName,
      yearOfPassing,
      gradeOrPercentage,
      file,
      fileName,
    } = qualificationDraft;

    const trimmedCourseName = (courseName || '').trim();
    const isCourseNameRequired = (qualificationType || '').toUpperCase() !== '10';

    if (
      !qualificationType ||
      (isCourseNameRequired && !trimmedCourseName) ||
      !institutionName ||
      !yearOfPassing ||
      !file
    ) {
      setQualificationError(
        'Please complete all required fields. Course name is optional for 10th standard qualifications.'
      );
      return;
    }

    if (yearOfPassing.length !== 4) {
      setQualificationError('Year of passing must be four digits.');
      return;
    }

    try {
      setIsQualificationUploading(true);
      setQualificationError('');

      const payload = new FormData();
      payload.append('file', file);

      const { data: upload } = await apiRequest('/api/public/education-qualifications/upload', {
        method: 'POST',
        body: payload,
        skipAuthRedirect: true,
      });

      const nextEntry = {
        localId: createLocalId(),
        qualificationType,
        courseName: trimmedCourseName ? trimmedCourseName.toUpperCase() : '',
        institutionName,
        yearOfPassing,
        gradeOrPercentage,
        documentPath: upload?.filePath || '',
        documentUrl: upload?.fileUrl || '',
        fileName: (upload?.fileName || fileName || file.name || '').toUpperCase(),
      };

      setQualificationEntries((prev) => [...prev, nextEntry]);
      resetQualificationDraft();
    } catch (error) {
      setQualificationError(error.message || 'Failed to upload qualification document.');
    } finally {
      setIsQualificationUploading(false);
    }
  };

  const handleRemoveQualification = (localId) => {
    setQualificationEntries((prev) => prev.filter((entry) => entry.localId !== localId));
    setQualificationError('');
  };

  const handleViewQualification = (entry) => {
    const url = resolveDocumentUrl(entry);
    if (!url) {
      window.alert('Document preview is not available for this qualification.');
      return;
    }

    window.open(url, '_blank', 'noopener');
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (!hasQualification) {
      setQualificationError('');
      onSubmit({
        education: {
          hasQualification: false,
          qualifications: [],
        },
      });
      return;
    }

    const sanitizedEntries = sanitizeQualificationEntries(qualificationEntries);

    if (enableValidation && sanitizedEntries.length === 0) {
      setQualificationError('Add at least one qualification entry.');
      return;
    }

    setQualificationError('');
    onSubmit({
      education: {
        hasQualification,
        qualifications: sanitizedEntries,
      },
    });
  };

  const handleBack = () => {
    if (!hasQualification) {
      onSubmit({
        education: {
          hasQualification: false,
          qualifications: [],
        },
        goBack: true,
      });
      return;
    }

    const sanitizedEntries = sanitizeQualificationEntries(qualificationEntries);
    onSubmit({
      education: {
        hasQualification,
        qualifications: sanitizedEntries,
      },
      goBack: true,
    });
  };

  return (
    <form onSubmit={handleFormSubmit} className="d-flex flex-column" style={{ minHeight: '80vh' }}>
      <div className="row flex-grow-1">
        <div className="col-md-8">
          <div className="row g-4">
            <div className="col-12">
              <div className="border rounded-3 p-3 bg-light">
                <h5 className="fw-semibold mb-3">Educational Qualification</h5>
                <p className="text-muted mb-3">
                  Do you have educational qualifications to add?
                </p>
                <div className="d-flex flex-column flex-md-row gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="hasQualification"
                      id="qualificationYes"
                      checked={hasQualification === true}
                      onChange={() => {
                        setHasQualification(true);
                        setQualificationError('');
                      }}
                    />
                    <label className="form-check-label" htmlFor="qualificationYes">
                      Yes
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="hasQualification"
                      id="qualificationNo"
                      checked={hasQualification === false}
                      onChange={() => {
                        setHasQualification(false);
                        setQualificationEntries([]);
                        resetQualificationDraft();
                        setQualificationError('');
                      }}
                    />
                    <label className="form-check-label" htmlFor="qualificationNo">
                      No
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {hasQualification && (
              <>
                <div className="col-12">
                  <div className="border rounded-3 p-3 bg-light">
                    <h6 className="fw-semibold mb-3">Add Educational Qualification</h6>
                    <div className="d-flex flex-column flex-md-row align-items-md-end gap-3">
                      <div className="flex-fill">
                        <label htmlFor="qualificationType" className="form-label fw-semibold">
                          Qualification Type
                        </label>
                        <select
                          id="qualificationType"
                          name="qualificationType"
                          className="form-select"
                          value={qualificationDraft.qualificationType}
                          onChange={handleQualificationDraftChange}
                        >
                          <option value="">Select Qualification</option>
                          {qualificationOptions.map((option) => (
                            <option key={option.id || option.code} value={(option.code || '').toUpperCase()}>
                              {(option.title || option.code || '').toUpperCase()}
                            </option>
                          ))}
                        </select>
                        {qualificationOptionsError && (
                          <div className="text-danger small mt-2">
                            {qualificationOptionsError}
                          </div>
                        )}
                      </div>
                      <div className="flex-fill">
                        <label htmlFor="courseName" className="form-label fw-semibold">
                          Course Name
                        </label>
                        <input
                          type="text"
                          id="courseName"
                          name="courseName"
                          className="form-control"
                          placeholder="Enter course"
                          value={qualificationDraft.courseName}
                          onChange={handleQualificationDraftChange}
                        />
                      </div>
                      <div className="flex-fill">
                        <label htmlFor="institutionName" className="form-label fw-semibold">
                          Institution Name
                        </label>
                        <input
                          type="text"
                          id="institutionName"
                          name="institutionName"
                          className="form-control"
                          placeholder="Enter institution"
                          value={qualificationDraft.institutionName}
                          onChange={handleQualificationDraftChange}
                        />
                      </div>
                    </div>

                    <div className="row g-3 mt-3">
                      <div className="col-md-4">
                        <label htmlFor="yearOfPassing" className="form-label fw-semibold">
                          Year of Passing
                        </label>
                        <input
                          type="text"
                          id="yearOfPassing"
                          name="yearOfPassing"
                          className="form-control"
                          placeholder="YYYY"
                          value={qualificationDraft.yearOfPassing}
                          onChange={handleQualificationDraftChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label htmlFor="gradeOrPercentage" className="form-label fw-semibold">
                          Grade / Percentage
                        </label>
                        <input
                          type="text"
                          id="gradeOrPercentage"
                          name="gradeOrPercentage"
                          className="form-control"
                          placeholder="E.g. 75%"
                          value={qualificationDraft.gradeOrPercentage}
                          onChange={handleQualificationDraftChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label htmlFor="qualificationDocument" className="form-label fw-semibold">
                          Upload Document
                        </label>
                        <input
                          type="file"
                          id="qualificationDocument"
                          name="file"
                          className="form-control"
                          accept=".pdf,.jpg,.jpeg,.png"
                          ref={qualificationFileInputRef}
                          onChange={handleQualificationDraftChange}
                        />
                      </div>
                    </div>

                    {qualificationDraft.fileName && (
                      <div className="mt-2 text-muted small">
                        Selected file: {qualificationDraft.fileName}
                      </div>
                    )}

                    {qualificationError && (
                      <div className="text-danger small mt-2">{qualificationError}</div>
                    )}

                    <button
                      type="button"
                      className="btn btn-primary w-100 mt-3"
                      onClick={handleAddQualification}
                      disabled={isQualificationUploading}
                    >
                      {isQualificationUploading ? 'Uploading...' : 'Add Qualification'}
                    </button>
                  </div>
                </div>

                {qualificationEntries.length > 0 && (
                  <div className="col-12">
                    <div className="d-flex flex-column gap-3">
                      {qualificationEntries.map((entry, index) => {
                        const label = getQualificationLabel(entry.qualificationType);
                        const url = resolveDocumentUrl(entry);
                        return (
                          <div
                            key={entry.localId}
                            className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 p-3 border rounded-3"
                          >
                            <div className="d-flex flex-column">
                              <span className="fw-semibold text-uppercase">
                                {index + 1}. {label}
                              </span>
                              <span className="text-muted small">
                                {entry.courseName || 'Course'} | {entry.institutionName || 'Institution'}
                              </span>
                              <span className="text-muted small">
                                Year: {entry.yearOfPassing || 'N/A'}
                                {entry.gradeOrPercentage && ` | Grade: ${entry.gradeOrPercentage}`}
                              </span>
                              <span className="text-muted small">
                                {entry.fileName || 'No document attached'}
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-light btn-sm rounded-circle"
                                onClick={() => handleViewQualification(entry)}
                                disabled={!url}
                                title={url ? 'View document' : 'Preview unavailable'}
                              >
                                <Eye />
                              </button>
                              <button
                                type="button"
                                className="btn btn-light btn-sm rounded-circle text-danger"
                                onClick={() => handleRemoveQualification(entry.localId)}
                                title="Remove qualification"
                              >
                                <XLg />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <div
            className="position-fixed end-0 d-none d-md-block"
            style={{
              maxWidth: '400px',
              zIndex: 1,
              bottom: '100px',
            }}
          >
            <img
              src={employeeForm4}
              alt="Education Information"
              className="img-fluid"
              style={{ maxHeight: '45vh' }}
            />
          </div>
        </div>
      </div>

      <div className="mt-auto pt-4 d-flex justify-content-between">
        <button type="button" onClick={handleBack} className="btn btn-outline-secondary px-4">
          Back
        </button>
        <button type="submit" className="btn btn-danger px-4">
          Next
        </button>
      </div>
    </form>
  );
};

export default EducationForm;
